import { z } from 'zod';

// ─── Enums ────────────────────────────────────────────────────────────────────

export const valueTypeEnumSchema = z.enum(['integer', 'float', 'string', 'bool', 'json']);
export type ValueTypeEnum = z.infer<typeof valueTypeEnumSchema>;

// ─── Parameter definition ─────────────────────────────────────────────────────

export const deviceParameterRoleSchema = z.enum(['timeseries', 'parameter']);
export type DeviceParameterRole = z.infer<typeof deviceParameterRoleSchema>;

export const valueValidatorRulesSchema = z
  .object({
    min: z.number().optional(),
    max: z.number().optional(),
    enum: z.array(z.union([z.string(), z.number()])).optional(),
    pattern: z.string().optional(),
    minLength: z.number().int().optional(),
    maxLength: z.number().int().optional(),
    multipleOf: z.number().optional(),
  })
  .nullable()
  .optional();
export type ValueValidatorRules = z.infer<typeof valueValidatorRulesSchema>;

export const deviceParameterDefinitionSchema = z.object({
  id: z.number(),
  name: z.string(),
  comment: z.string().nullable().optional(),
  role: deviceParameterRoleSchema,
  variable: z.string(),
  value: z.string().optional(),
  valueType: valueTypeEnumSchema,
  units: z.string().nullable(),
  order: z.number().optional(),
  valueValidatorRules: valueValidatorRulesSchema,
});
export type DeviceParameterDefinition = z.infer<typeof deviceParameterDefinitionSchema>;

// ─── Device types ─────────────────────────────────────────────────────────────

const deviceGroupClassSchema = z.object({ id: z.number(), name: z.string() });

const deviceGroupSchema = z.object({
  id: z.number(),
  name: z.string(),
  groupClass: deviceGroupClassSchema,
  parent: z.number().nullable(),
});
export type DeviceGroup = z.infer<typeof deviceGroupSchema>;

export const deviceTypeSchema = z.object({
  id: z.number(),
  name: z.string(),
  parameterdefinitionSet: z.array(deviceParameterDefinitionSchema),
  deviceGroup: deviceGroupSchema,
});
export type DeviceType = z.infer<typeof deviceTypeSchema>;

// ─── Device ───────────────────────────────────────────────────────────────────

export const deviceSchema = z.object({
  id: z.number(),
  name: z.string(),
  deviceType: z.number(),
  energySystem: z.number(),
  parent: z.number().nullable(),
});
export type Device = z.infer<typeof deviceSchema>;

export const createDeviceRequestSchema = z.object({
  name: z.string().max(100),
  deviceType: z.number(),
  energySystem: z.number(),
  parent: z.number().nullable(),
});
export type CreateDeviceRequest = z.infer<typeof createDeviceRequestSchema>;

export const updateDeviceRequestSchema = createDeviceRequestSchema
  .omit({ energySystem: true })
  .partial();
export type UpdateDeviceRequest = z.infer<typeof updateDeviceRequestSchema>;

// ─── Device parameters ────────────────────────────────────────────────────────

export const deviceParameterSchema = z.object({
  parameterDefinition: deviceParameterDefinitionSchema,
  value: z.string().max(255),
});
export type DeviceParameter = z.infer<typeof deviceParameterSchema>;

export const createDeviceParameterSchema = z.object({
  parameterDefinition: z.number(),
  value: z.string().max(255),
});
export type CreateDeviceParameter = z.infer<typeof createDeviceParameterSchema>;

export const updateDeviceParameterRequestSchema = z.object({
  value: z.string().max(255),
});
export type UpdateDeviceParameterRequest = z.infer<typeof updateDeviceParameterRequestSchema>;
